/**
 * The app's toasts: what the toasts domain holds, drawn as the ui Toast in
 * its stack, newest on top.
 *
 * Everything a toast looks like and where it sits is the primitive's
 * (components/ui/toast.tsx): the card and its mark, the corner above the page
 * and clear of the shell, the live regions. This layer is the game's side of
 * it — which tone each kind of toast takes, the close button's name in the
 * player's language, and the order. The store takes a toast away once its
 * time is up.
 *
 * The stack renders while it is empty too: it keeps the two live regions each
 * toast says its own line into as it arrives — at once for a warning or a
 * danger, when the reader is free for the rest — and a region that arrives
 * with its words already in it is not announced.
 */

import { useTranslation } from 'react-i18next';

import { dismissToast, useToastsStore, type ToastType } from '../../domains/toasts';
import { Toast, ToastStack, type ToastProps } from '../ui/toast';

/** The tone each kind of toast takes: an error is danger's coral. */
const TONE: Record<ToastType, NonNullable<ToastProps['tone']>> = {
  success: 'success',
  error: 'danger',
  info: 'info',
  warning: 'warning',
};

export function ToastContainer() {
  const { t } = useTranslation();
  const toasts = useToastsStore((state) => state.toasts);

  // The store keeps them in the order they came; the newest goes on top.
  const newestFirst = [...toasts].reverse();

  return (
    <ToastStack>
      {newestFirst.map((toast) => (
        <Toast
          key={toast.id}
          tone={TONE[toast.type]}
          title={toast.title}
          detail={toast.message}
          onDismiss={() => dismissToast(toast.id)}
          dismissLabel={t('common.close')}
        />
      ))}
    </ToastStack>
  );
}
